import { SpreadsheetData } from "@via/components/spreadsheet-editor/spreadsheet-editor.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates that a vmeta project is selected
 */
export function validateVmetaProject(
  selectedVmetaProject: SelectOption | null
): ValidationResult {
  if (!selectedVmetaProject?.value) {
    return {
      isValid: false,
      errors: ["Please select a metadata tracker"],
    };
  }
  return { isValid: true, errors: [] };
}

/**
 * Validates that a collection ID is provided
 */
export function validateCollectionId(
  collectionId: string | null | undefined
): ValidationResult {
  if (!collectionId) {
    return {
      isValid: false,
      errors: ["No collection selected"],
    };
  }
  return { isValid: true, errors: [] };
}

/**
 * Validates that spreadsheet data contains at least one row with data
 */
export function validateSpreadsheetData(
  spreadsheetData: SpreadsheetData,
  _columnLabels: string[]
): ValidationResult {
  const errors: string[] = [];

  if (spreadsheetData.length === 0) {
    errors.push("No data to save");
    return { isValid: false, errors };
  }

  // Check if all rows are empty
  const hasData = spreadsheetData.some(row =>
    row.some(cell => cell?.value && cell.value.trim() !== "")
  );

  if (!hasData) {
    errors.push("All rows are empty. Please add at least one row with data.");
  }

  // Check for required columns (if any are marked as required)
  // This could be extended based on your requirements

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validates all inputs before saving files
 */
export function validateBeforeSave(
  selectedVmetaProject: SelectOption | null,
  collectionId: string | null | undefined,
  spreadsheetData: SpreadsheetData,
  columnLabels: string[]
): ValidationResult {
  const errors: string[] = [];

  const projectValidation = validateVmetaProject(selectedVmetaProject);
  if (!projectValidation.isValid) {
    errors.push(...projectValidation.errors);
  }

  const collectionValidation = validateCollectionId(collectionId);
  if (!collectionValidation.isValid) {
    errors.push(...collectionValidation.errors);
  }

  const dataValidation = validateSpreadsheetData(spreadsheetData, columnLabels);
  if (!dataValidation.isValid) {
    errors.push(...dataValidation.errors);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
